import React from 'react';
import { addReactError } from '@datadog/browser-rum-react';
import { v4 } from 'uuid';
import {
    Flex,
    HStack,
    TextStyle,
    useToast,
    VStack,
    IconButton,
    InlineBanner,
    Text,
} from '@cad-arc';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { logError } from './log';

type InlineBannerProps = React.ComponentProps<typeof InlineBanner>;
export type ErrorBoundaryName =
    | 'GLOBAL'
    | 'PANEL'
    | 'UNKNOWN'
    | 'DRAWER'
    | 'MODAL'
    | 'FACTORY_MODAL'
    | 'EVENT_CONTEXT_MENU'
    | 'UNIT_CONTEXT_MENU'
    | 'HELD_UNIT_CONTEXT_MENU'
    | 'HOSPITAL_CONTEXT_MENU'
    | 'MULTI_UNIT_CONTEXT_MENU'
    | 'UNIT_TIMERS_CONTEXT_MENU'
    | 'SCHEDULED_EVENT_CONTEXT_MENU'
    | 'RAPID_SOS_ADDITIONAL_INFO'
    | 'EVENT_PANEL_ADDITIONAL_INFOS'
    | 'EVENT_PANEL_TAB'
    | 'PRO_QA_ADDITIONAL_INFOS'
    | 'SEARCH_WINDOW_FORM'
    | 'SEARCH_RESULTS_TAB'
    | 'SAVED_SEARCHES'
    | 'UNIT_SEARCH_RESULTS'
    | 'FR_EVENTS_LIST_PAGE'
    | 'BOLO_PANEL_TAB'
    | 'BOLO_PANEL_ADDITIONAL_INFOS'
    | 'DEX_RESULTS_LEFT_PANE'
    | 'DEX_RESULTS_RIGHT_PANE'
    | 'FR_DEX_NEW_SEARCH'
    | 'FR_ASSIGNED_EVENT'
    | 'FEDERATED_SEARCH_QUERY_RESULTS'
    | 'FEDERATED_SEARCH_MORE_ACTIONS_MENU'
    | 'FEDERATED_SEARCH_NOTIFICATIONS_SYNC'
    | 'FEDERATED_SEARCH_QUERY_FORM'
    | 'FEDERATED_SEARCH_RESULTS_PANEL'
    | 'FR_FEDERATED_SEARCH_CLIENT';

export function ErrorBoundaryBanner({
    eventId,
    actions,
    messageOverride,
    messageVerbNoun,
}: {
    eventId?: string;
    actions?: InlineBannerProps['actions'];
    messageVerbNoun?: string;
    messageOverride?: string;
}) {
    const toast = useToast();
    return (
        <Flex w="100%" h="100%" justifyContent="center" alignItems="center" padding="24px">
            <InlineBanner
                title="An Error Occurred"
                status="error"
                description={
                    <VStack alignItems="flex-start">
                        <Text>{messageOverride ?? errorOccurredWhile(messageVerbNoun)}</Text>
                        {eventId && (
                            <HStack>
                                <Text>
                                    Error ID: <TextStyle fontWeight="bold">{eventId}</TextStyle>
                                </Text>
                                <IconButton
                                    style={{
                                        color: 'var(--arc-banner-accentContent-color)',
                                    }}
                                    size="sm"
                                    aria-label="Copy to clipboard"
                                    icon="Duplicate"
                                    variant="ghost"
                                    onClick={() => {
                                        navigator.clipboard.writeText(eventId);
                                        toast({
                                            status: 'default',
                                            description: 'Error ID copied to clipboard.',
                                        });
                                    }}
                                />
                            </HStack>
                        )}
                    </VStack>
                }
                actions={actions}
            />
        </Flex>
    );
}

type ErrorBoundaryProps = {
    actions?: InlineBannerProps['actions'];
    name: ErrorBoundaryName;
    messageVerbNoun?: string;
    messageOverride?: string;
    asToast?: boolean;
    extraTags?: Record<string, string>;
    onError?: () => void;
    fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
    children: React.ReactNode;
};

type ErrorBoundaryFallbackProps = {
    error: Error;
    resetError: () => void;
    errorId: string;
    shouldResetOnError: boolean;
    actions?: InlineBannerProps['actions'];
    messageVerbNoun?: string;
    messageOverride?: string;
    asToast?: boolean;
};

const ErrorBoundaryFallback: React.FC<ErrorBoundaryFallbackProps> = ({
    resetError,
    errorId,
    shouldResetOnError,
    actions,
    messageVerbNoun,
    messageOverride,
    asToast,
}) => {
    const toast = useToast();
    const lastErrorIdRef = React.useRef<string | null>(null);

    React.useEffect(() => {
        if (!asToast || lastErrorIdRef.current === errorId) {
            return;
        }
        lastErrorIdRef.current = errorId;

        toast({
            status: 'error',
            description: (
                <InlineBanner
                    status="error"
                    title={
                        <Text
                            style={{
                                color: 'var(--arc-banner-content-color)',
                            }}
                        >
                            An Error Occurred
                        </Text>
                    }
                    description={
                        <VStack
                            alignItems="flex-start"
                            style={{
                                color: 'var(--arc-banner-content-color)',
                            }}
                        >
                            <Text>{messageOverride ?? errorOccurredWhile(messageVerbNoun)}</Text>
                            <HStack>
                                <Text>
                                    Error ID: <TextStyle fontWeight="bold">{errorId}</TextStyle>
                                </Text>
                                <IconButton
                                    style={{ color: 'var(--arc-banner-content-color)' }}
                                    size="sm"
                                    aria-label="Copy to clipboard"
                                    icon="Duplicate"
                                    variant="ghost"
                                    onClick={() => {
                                        navigator.clipboard.writeText(errorId);
                                        toast({
                                            status: 'default',
                                            description: 'Error ID copied to clipboard.',
                                        });
                                    }}
                                />
                            </HStack>
                        </VStack>
                    }
                />
            ),
        });

        if (shouldResetOnError) {
            resetError();
        }
    }, [asToast, errorId, messageOverride, messageVerbNoun, resetError, shouldResetOnError, toast]);

    if (asToast) {
        return <></>;
    }

    return (
        <ErrorBoundaryBanner
            eventId={errorId}
            actions={actions}
            messageOverride={messageOverride}
            messageVerbNoun={messageVerbNoun}
        />
    );
};

type ErrorBoundaryState =
    | { didCatch: false; error: null; errorId: null }
    | { didCatch: true; error: Error; errorId: string };

const INITIAL_STATE: ErrorBoundaryState = { didCatch: false, error: null, errorId: null };

class CadErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = INITIAL_STATE;

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { didCatch: true, error, errorId: v4() };
    }

    resetError = () => {
        this.setState(INITIAL_STATE);
    };

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        const errorId = this.state.errorId ?? v4();
        addReactError(error, errorInfo);
        logError(error, {
            errorBoundaryName: this.props.name,
            errorId,
            messageVerbNoun: this.props.messageVerbNoun,
            messageOverride: this.props.messageOverride,
            ...this.props.extraTags,
        });
        this.props.onError?.();
    }

    render() {
        const {
            actions,
            messageVerbNoun,
            messageOverride,
            asToast,
            onError,
            children,
        } = this.props;

        if (this.state.didCatch && this.state.error && this.state.errorId) {
            if (this.props.fallback) {
                const CustomFallback = this.props.fallback;
                return <CustomFallback error={this.state.error} resetError={this.resetError} />;
            }
            return (
                <ErrorBoundaryFallback
                    error={this.state.error}
                    resetError={this.resetError}
                    errorId={this.state.errorId}
                    shouldResetOnError={Boolean(onError)}
                    actions={actions}
                    messageVerbNoun={messageVerbNoun}
                    messageOverride={messageOverride}
                    asToast={asToast}
                />
            );
        }

        return children;
    }
}

export const ErrorBoundary = (props: ErrorBoundaryProps): React.ReactElement => {
    return <CadErrorBoundary {...props} />;
};
